import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { API } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChatCircle, X, Spinner, ArrowLeft, Stethoscope, Gear, Warning, CalendarBlank, Clock, MapPin, User, FileText, Trash, ClipboardText, Check, Copy, ShieldCheck, EnvelopeSimple, CalendarPlus, DownloadSimple
} from '@phosphor-icons/react';
import SendEmailModal from '../components/shared/SendEmailModal';

const downloadIcs = (appointmentId, title) => {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const link = document.createElement('a');
  link.href = `${API}/appointments/${appointmentId}/ics`;
  link.download = `kindra-${(title || 'appointment').replace(/\s+/g, '-').slice(0, 30)}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AppointmentDetailPage = ({ initialTab = null } = {}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [prepReport, setPrepReport] = useState(null);
  const [doctorBrief, setDoctorBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [briefCopied, setBriefCopied] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [emailModal, setEmailModal] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prepReviewed, setPrepReviewed] = useState(false);
  const [prepReviewing, setPrepReviewing] = useState(false);

  const fetchAppointment = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/${id}`);
      setAppointment(data);
      setFormData(data);
      setPrepReviewed(!!data.prep_reviewed_at);
      setLoading(false);
      // Load prep report in background (non-blocking)
      if (data.prep_status === 'ready' || initialTab === 'prep') {
        axios.get(`${API}/appointments/${id}/prep-report`).then(r => setPrepReport(r.data)).catch(() => {});
      }
      // If this was deep-linked from the email CTA, track the view + scroll to prep
      if (initialTab === 'prep') {
        try {
          await axios.post(`${API}/activation/track`, {
            event: 'prep_card_viewed',
            props: { appointment_id: id, source: 'email_cta' },
          });
        } catch { /* non-blocking */ }
        setTimeout(() => {
          const el = document.querySelector('[data-testid="appointment-prep-section"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [id, initialTab]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const generatePrepReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await axios.get(`${API}/appointments/${id}/prep-report`);
      setPrepReport(data);
    } catch (err) {
      console.error(err);
    } finally {
      setReportLoading(false);
    }
  };

  const markPrepReviewed = async () => {
    if (prepReviewed || prepReviewing) return;
    setPrepReviewing(true);
    try {
      await axios.post(`${API}/appointments/${id}/prep/mark-reviewed`);
      setPrepReviewed(true);
    } catch (err) {
      console.error('[mark-reviewed]', err);
    } finally {
      setPrepReviewing(false);
    }
  };

  const fetchDoctorBrief = async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/${id}/doctor-brief`);
      setDoctorBrief(data);
    } catch { /* ignore */ }
  };

  const copyBriefToClipboard = async () => {
    if (!doctorBrief) return;
    const text = [
      `Patient: ${doctorBrief.patient}`,
      `Appointment: ${doctorBrief.appointment} — ${doctorBrief.date} ${doctorBrief.time || ''}`,
      '',
      `Conditions: ${doctorBrief.conditions?.join(', ') || 'None'}`,
      `Medications: ${doctorBrief.medications?.join('; ') || 'None'}`,
      `Symptoms: ${doctorBrief.symptoms?.join(', ') || 'None'}`,
      '',
      doctorBrief.recent_changes?.length ? `Recent changes:\n${doctorBrief.recent_changes.map(c => `  - ${c}`).join('\n')}` : '',
      doctorBrief.risk_flags?.length ? `Risk flags:\n${doctorBrief.risk_flags.map(r => `  - ${r}`).join('\n')}` : '',
      doctorBrief.discussion_points?.length ? `Discussion points:\n${doctorBrief.discussion_points.map(p => `  - ${p}`).join('\n')}` : '',
    ].filter(Boolean).join('\n');

    await navigator.clipboard.writeText(text);
    setBriefCopied(true);
    setTimeout(() => setBriefCopied(false), 2000);
  };

  const updateAppointment = async () => {
    setSaving(true);
    try {
      const { title, date, time, status, clinician_name, clinic_name, specialty, purpose, outcome, linked_conditions } = formData;
      await axios.put(`${API}/appointments/${id}`, { title, date, time, status, clinician_name, clinic_name, specialty, purpose, outcome, linked_conditions });
      await fetchAppointment();
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteAppointment = async () => {
    if (window.confirm('Delete this appointment?')) {
      try {
        await axios.delete(`${API}/appointments/${id}`);
        navigate('/appointments');
      } catch (err) {
        console.error(err);
      }
    }
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      await axios.put(`${API}/appointments/${id}`, { status: 'completed' });
      await fetchAppointment();
    } catch (err) {
      console.error(err);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Spinner size={32} className="animate-spin text-[#235347]" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center">
        <p className="text-slate-500">Appointment not found</p>
        <button onClick={() => navigate('/appointments')} className="mt-4 text-[#235347]">Go back</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col pb-8">
      <header className="glass-header sticky top-0 z-10 px-4 py-3 flex items-center justify-between safe-top min-h-[48px]">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/appointments')} className="text-slate-600 hover:text-slate-900 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="font-medium text-slate-900 truncate">{appointment.title}</h1>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setShowEditModal(true)} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-slate-100 rounded-lg" data-testid="edit-appointment-btn">
            <Gear size={20} className="text-slate-600" />
          </button>
          <button onClick={deleteAppointment} className="p-2 hover:bg-red-50 rounded-lg" data-testid="delete-appointment-btn">
            <Trash size={20} className="text-red-500" />
          </button>
        </div>
      </header>

      <div className="flex-1 px-6 py-4 space-y-6">
        {/* Status & Date */}
        <div className="card-base p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                appointment.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                appointment.status === 'completed' ? 'bg-green-100 text-green-700' :
                'bg-red-100 text-red-700'
              }`}>
                {appointment.status}
              </span>
              {appointment.prep_status === 'ready' && (
                <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-200 text-xs font-medium rounded-full flex items-center gap-1" data-testid="prep-status-badge">
                  <ShieldCheck size={12} weight="duotone" /> Report ready
                </span>
              )}
              {appointment.prep_status === 'generating' && (
                <span className="px-2.5 py-0.5 bg-blue-50 text-blue-500 border border-blue-200 text-xs rounded-full flex items-center gap-1">
                  <Spinner size={12} className="animate-spin" /> Preparing
                </span>
              )}
            </div>
            {appointment.status === 'upcoming' && (
              <button
                onClick={markComplete}
                disabled={completing}
                className="text-sm text-[#235347] font-medium hover:underline flex items-center gap-1"
                data-testid="mark-complete-btn"
              >
                {completing ? <Spinner size={14} className="animate-spin" /> : <Check size={14} />}
                Mark as completed
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <CalendarBlank size={20} className="text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="font-medium text-slate-900">
                  {new Date(appointment.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
            {appointment.time && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Clock size={20} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Time</p>
                  <p className="font-medium text-slate-900">{appointment.time}</p>
                </div>
              </div>
            )}
          </div>

          {/* Add to Calendar */}
          {appointment.status === 'upcoming' && (
            <button
              onClick={() => downloadIcs(appointment.id, appointment.title)}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-[#235347]/5 hover:bg-[#235347]/10 text-[#235347] rounded-xl text-sm font-medium transition-colors"
              data-testid="add-to-calendar-btn"
            >
              <CalendarPlus size={16} weight="duotone" />
              Add to Calendar
            </button>
          )}
        </div>

        {/* Details */}
        <div className="card-base p-5 space-y-4">
          {appointment.clinician_name && (
            <div className="flex items-center gap-3">
              <User size={20} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Clinician</p>
                <p className="text-slate-900">{appointment.clinician_name}</p>
              </div>
            </div>
          )}
          {appointment.clinic_name && (
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Location</p>
                <p className="text-slate-900">{appointment.clinic_name}</p>
              </div>
            </div>
          )}
          {appointment.specialty && (
            <div className="flex items-center gap-3">
              <Stethoscope size={20} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Specialty</p>
                <p className="text-slate-900">{appointment.specialty}</p>
              </div>
            </div>
          )}
          {appointment.purpose && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Purpose</p>
              <p className="text-slate-700">{appointment.purpose}</p>
            </div>
          )}
        </div>

        {/* Linked Conditions */}
        {appointment.linked_conditions?.length > 0 && (
          <div className="card-base p-5">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Related Conditions</h3>
            <div className="flex flex-wrap gap-2">
              {appointment.linked_conditions.map((c, i) => (
                <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-700">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Linked Documents */}
        {appointment.documents?.length > 0 && (
          <div className="card-base p-5">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Linked Documents</h3>
            <div className="space-y-2">
              {appointment.documents.map(doc => (
                <div key={doc.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  <FileText size={20} className="text-slate-500" />
                  <span className="text-sm text-slate-700 flex-1 truncate">{doc.filename}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preparation Report */}
        {appointment.status === 'upcoming' && (
          <div className="card-base p-5" data-testid="appointment-prep-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-900">Preparation Report</h3>
              <div className="flex items-center gap-2">
                {!prepReport && (
                  <button
                    onClick={generatePrepReport}
                    disabled={reportLoading}
                    className="text-sm text-[#235347] font-medium hover:underline flex items-center gap-1"
                    data-testid="generate-report-btn"
                  >
                    {reportLoading ? <Spinner size={16} className="animate-spin" /> : <ClipboardText size={16} />}
                    Generate Report
                  </button>
                )}
                {!doctorBrief && (
                  <button
                    onClick={fetchDoctorBrief}
                    className="text-sm text-blue-600 font-medium hover:underline flex items-center gap-1"
                    data-testid="doctor-brief-btn"
                  >
                    <FileText size={16} /> Doctor Brief
                  </button>
                )}
                {prepReport && !prepReviewed && (
                  <button
                    onClick={markPrepReviewed}
                    disabled={prepReviewing}
                    className="text-sm text-[#235347] font-medium hover:underline flex items-center gap-1 disabled:opacity-50"
                    data-testid="mark-prep-reviewed-btn"
                  >
                    {prepReviewing ? <Spinner size={16} className="animate-spin" /> : <Check size={16} />}
                    Mark as reviewed
                  </button>
                )}
                {prepReport && prepReviewed && (
                  <span className="text-xs font-medium text-[#235347] flex items-center gap-1" data-testid="prep-reviewed-indicator">
                    <Check size={14} /> Reviewed
                  </span>
                )}
                {prepReport && (
                  <button
                    onClick={() => setEmailModal('prep_report')}
                    className="text-sm text-[#235347] font-medium hover:underline flex items-center gap-1"
                    data-testid="email-prep-report-btn"
                  >
                    <EnvelopeSimple size={16} /> Send
                  </button>
                )}
              </div>
            </div>

            {prepReport ? (
              <div className="space-y-4">
                {prepReport.ai_insights?.summary && (
                  <div className="p-4 bg-[#235347]/5 rounded-xl">
                    <p className="text-slate-700">{prepReport.ai_insights.summary}</p>
                  </div>
                )}
                
                {prepReport.ai_insights?.key_concerns?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Key Concerns</h4>
                    <ul className="space-y-1">
                      {prepReport.ai_insights.key_concerns.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <Warning size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {prepReport.ai_insights?.suggested_questions?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Questions to Ask</h4>
                    <ul className="space-y-1">
                      {prepReport.ai_insights.suggested_questions.map((q, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <ChatCircle size={16} className="text-[#235347] mt-0.5 flex-shrink-0" />
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {prepReport.trends && (
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Recent Trends</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-medium text-slate-900">{prepReport.trends.mood || '-'}</p>
                        <p className="text-xs text-slate-500">Mood</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-medium text-slate-900">{prepReport.trends.sleep || '-'}h</p>
                        <p className="text-xs text-slate-500">Sleep</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <p className="text-lg font-medium text-slate-900">{prepReport.trends.energy || '-'}</p>
                        <p className="text-xs text-slate-500">Energy</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Generate a personalized preparation report based on your health data to help you make the most of this appointment.</p>
            )}

            {/* Doctor Brief */}
            {doctorBrief && (
              <div className="mt-5 pt-5 border-t border-slate-100" data-testid="doctor-brief-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Doctor-Facing Brief</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEmailModal('doctor_brief')}
                      className="text-xs text-[#235347] hover:text-[#1A3F36] flex items-center gap-1 transition-colors"
                      data-testid="email-doctor-brief-btn"
                    >
                      <EnvelopeSimple size={12} /> Email
                    </button>
                    <button
                      onClick={copyBriefToClipboard}
                      className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
                      data-testid="copy-brief-btn"
                    >
                      {briefCopied ? <><Check size={12} className="text-emerald-500" /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm">
                  <div>
                    <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Conditions</p>
                    <p className="text-slate-700">{doctorBrief.conditions?.join(', ') || 'None'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Medications</p>
                    {doctorBrief.medications?.length > 0
                      ? doctorBrief.medications.map((m, i) => <p key={i} className="text-slate-700 text-xs">{m}</p>)
                      : <p className="text-slate-500 text-xs">None</p>
                    }
                  </div>
                  {doctorBrief.symptoms?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Active Symptoms</p>
                      <p className="text-slate-700 text-xs">{doctorBrief.symptoms.join(', ')}</p>
                    </div>
                  )}
                  {doctorBrief.recent_changes?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Recent Changes</p>
                      {doctorBrief.recent_changes.map((c, i) => (
                        <p key={i} className="text-slate-600 text-xs flex items-start gap-1.5">
                          <span className="text-slate-400 mt-0.5">-</span> {c}
                        </p>
                      ))}
                    </div>
                  )}
                  {doctorBrief.risk_flags?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Risk Flags</p>
                      {doctorBrief.risk_flags.map((r, i) => (
                        <p key={i} className="text-amber-700 text-xs flex items-start gap-1.5">
                          <Warning size={12} className="text-amber-500 mt-0.5 flex-shrink-0" /> {r}
                        </p>
                      ))}
                    </div>
                  )}
                  {doctorBrief.discussion_points?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Discussion Points</p>
                      {doctorBrief.discussion_points.map((p, i) => (
                        <p key={i} className="text-slate-700 text-xs flex items-start gap-1.5">
                          <ChatCircle size={12} className="text-[#235347] mt-0.5 flex-shrink-0" /> {p}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Outcome (for completed) */}
        {appointment.status === 'completed' && appointment.outcome && (
          <div className="card-base p-5">
            <h3 className="text-sm font-medium text-slate-900 mb-2">Outcome</h3>
            <p className="text-slate-700">{appointment.outcome}</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full rounded-t-3xl p-6 pb-24 max-h-[85vh] overflow-auto z-[60]"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-slate-900">Edit Appointment</h2>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <input
                  placeholder="Title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={formData.date || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  />
                  <input
                    type="time"
                    value={formData.time || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  />
                </div>
                <select
                  value={formData.status || 'upcoming'}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {formData.status === 'completed' && (
                  <textarea
                    placeholder="Outcome / Notes from visit"
                    value={formData.outcome || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, outcome: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[100px] resize-none"
                  />
                )}
              </div>

              <button
                onClick={updateAppointment}
                disabled={saving}
                className="w-full mt-6 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-appointment-btn"
              >
                {saving && <Spinner size={16} className="animate-spin" />}
                Save Changes
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Email Modal */}
      <AnimatePresence>
        {emailModal && (
          <SendEmailModal
            reportType={emailModal}
            reportId={id}
            defaultSubject={
              emailModal === 'prep_report'
                ? `Appointment Prep — ${appointment?.title || 'Appointment'}`
                : `Patient Brief — ${appointment?.title || 'Appointment'}`
            }
            onClose={() => setEmailModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};


export default AppointmentDetailPage;
